import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  InputBase,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import DoneRoundedIcon from '@mui/icons-material/DoneRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import SelectAllRoundedIcon from '@mui/icons-material/SelectAllRounded'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'

export default function BaseLocationPicker({ TaskBadge }) {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState(null)
  const [query, setQuery] = useState('')
  const [selectedLocationIDs, setSelectedLocationIDs] = useState([])

  const open = Boolean(anchorEl)

  const { user, setUser } = useContext(UserContext)
  const { baseLocationOptions, tenantInfo } = useContext(GlobalContext)

  const userSel = Array.isArray(user?.selectedBaseLocations)
    ? user.selectedBaseLocations
    : []
  const isSiteAdmin = user?.security?.isSiteAdmin === true
  const allIds = useMemo(
    () => baseLocationOptions.map((l) => l._id),
    [baseLocationOptions],
  )

  const getDefaultSelectedLocationIDs = () => {
    const validSelections = userSel
      .map((id) => String(id))
      .filter((id) => allIds.includes(id))

    if (validSelections.length) return validSelections
    return isSiteAdmin ? allIds : []
  }

  useEffect(() => {
    if (!baseLocationOptions.length) return

    setSelectedLocationIDs(getDefaultSelectedLocationIDs())
  }, [user, baseLocationOptions, anchorEl])

  const filteredLocations = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return baseLocationOptions
    return baseLocationOptions.filter((l) =>
      (l.name ?? '').toLowerCase().includes(q),
    )
  }, [baseLocationOptions, query])

  const handleOnClose = () => {
    setAnchorEl(null)
    setQuery('')
    setSelectedLocationIDs(getDefaultSelectedLocationIDs())
  }

  const toggleId = (locId) => {
    if (selectedLocationIDs.includes(locId)) {
      setSelectedLocationIDs(selectedLocationIDs.filter((x) => x !== locId))
    } else {
      setSelectedLocationIDs((prev) => [...prev, locId])
    }
  }

  const handleSelectAll = () => setSelectedLocationIDs(allIds)
  const handleClear = () => setSelectedLocationIDs([])

  const navy = theme.palette?.primary?.main ?? '#0B1F3B'
  const surfaceBorder = theme.palette?.divider
  const softBg = theme.palette?.action?.hover

  if (Number(tenantInfo?.subscriptionTier) < 2) return null
  if (baseLocationOptions.length <= 1) return null

  const handleSubmit = () => {
    API.put('/account/me', {
      selectedBaseLocations: selectedLocationIDs,
    }).then((res) => {
      if (res.status === 200) {
        setUser(res.data)
        setSelectedLocationIDs(
          Array.isArray(res.data?.selectedBaseLocations)
            ? res.data.selectedBaseLocations.map((id) => String(id))
            : [],
        )
        setAnchorEl(null)
        setQuery('')
      }
    })
  }

  return (
    <>
      <TaskBadge
        badgeContent={selectedLocationIDs.length}
        color="secondary"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <FilterListRoundedIcon
          sx={{ color: 'primary.main', cursor: 'pointer' }}
        />
      </TaskBadge>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleOnClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 420,
              borderRadius: 3,
              overflow: 'hidden',
              border: `1px solid ${surfaceBorder}`,
              boxShadow: '0 24px 80px rgba(0,0,0,0.18)',
            },
          },
        }}
        MenuListProps={{
          dense: true,
          sx: { py: 0 },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, pt: 1.75, pb: 1.25 }}>
          <Stack spacing={1.1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack spacing={0.25}>
                <Typography fontWeight={900} sx={{ lineHeight: 1.05 }}>
                  Base location filter
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Choose which locations you’re working with.
                </Typography>
              </Stack>

              <Chip
                size="small"
                label={`${selectedLocationIDs.length} selected`}
                sx={{
                  height: 26,
                  fontWeight: 900,
                  borderRadius: 2,
                  bgcolor: softBg,
                }}
              />
            </Stack>

            {/* Search */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 0.9,
                borderRadius: 2,
                border: `1px solid ${surfaceBorder}`,
                bgcolor: 'background.paper',
              }}
            >
              <SearchRoundedIcon fontSize="small" />
              <InputBase
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search locations…"
                sx={{ flex: 1, fontSize: 14 }}
                inputProps={{ 'aria-label': 'Search locations' }}
              />
              {query ? (
                <Button
                  size="small"
                  onClick={() => setQuery('')}
                  sx={{
                    minWidth: 0,
                    px: 1,
                    textTransform: 'none',
                    fontWeight: 800,
                  }}
                >
                  Clear
                </Button>
              ) : null}
            </Box>

            {/* Quick actions */}
            <Stack direction="row" spacing={1} justifyContent="flex-start">
              <Button
                size="small"
                startIcon={<SelectAllRoundedIcon />}
                onClick={handleSelectAll}
                disabled={!baseLocationOptions.length}
                sx={{ textTransform: 'none', fontWeight: 900 }}
              >
                Select all
              </Button>

              <Button
                size="small"
                startIcon={<ClearRoundedIcon />}
                onClick={handleClear}
                disabled={!selectedLocationIDs.length}
                sx={{ textTransform: 'none', fontWeight: 900 }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Divider />

        {/* List */}
        <Box sx={{ maxHeight: 340, overflow: 'auto' }}>
          {filteredLocations.length ? (
            filteredLocations.map((loc) => {
              const checked = selectedLocationIDs.includes(loc._id)

              return (
                <MenuItem
                  key={loc._id}
                  onClick={() => toggleId(loc._id)}
                  sx={{
                    px: 2,
                    py: 1,
                    gap: 1.25,
                    alignItems: 'center',
                    '&:hover': { bgcolor: softBg },
                  }}
                >
                  {/* Selection indicator (sleek, no checkbox clutter) */}
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: 1.25,
                      border: `1.5px solid ${checked ? navy : surfaceBorder}`,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: checked ? navy : 'transparent',
                      flex: '0 0 auto',
                    }}
                  >
                    {checked ? (
                      <DoneRoundedIcon sx={{ fontSize: 14, color: '#fff' }} />
                    ) : null}
                  </Box>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{ fontWeight: checked ? 900 : 700, lineHeight: 1.1 }}
                    >
                      {loc.name}
                    </Typography>
                    {/* Optional subtext hook if you add metadata later */}
                    {/* <Typography variant="caption" color="text.secondary">
                      {loc.region ?? ' '}
                    </Typography> */}
                  </Box>
                </MenuItem>
              )
            })
          ) : (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No locations match “{query}”.
              </Typography>
            </Box>
          )}
        </Box>

        <Divider />

        {/* Footer */}
        <Box sx={{ px: 2, py: 1.25 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Typography variant="caption" color="text.secondary">
              Tip: you can search by name and toggle multiple locations.
            </Typography>

            <Button
              size="small"
              onClick={handleSubmit}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: 2.25,
                bgcolor: navy,
                color: '#fff',
                '&:hover': { bgcolor: navy, filter: 'brightness(0.97)' },
              }}
              disabled={!selectedLocationIDs.length}
            >
              Done
            </Button>
          </Stack>
        </Box>
      </Menu>
    </>
  )
}
